/* The app's one panel shape: a bottom sheet on phones (thumb-reachable, full
 * width), a centered dialog on desktop. Used by the unified Settings panel in
 * App.jsx and by the control groups the Ledger contributes to it, so a setting
 * looks the same wherever it happens to live. */
import { C, ELEVATION, F, GHOST, R, T, chip } from "./theme.js";
import { useT } from "./i18n.js";

export function Sheet({ title, onClose, mobile, children }) {
  const translate = useT();
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 45,
        background: "rgba(10,15,12,0.55)",
        display: "grid",
        placeItems: mobile ? "end center" : "center",
        padding: mobile ? 0 : 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: C.card,
          border: `1px solid ${C.edge}`,
          borderRadius: mobile ? `${R.lg}px ${R.lg}px 0 0` : R.lg,
          boxShadow: ELEVATION.overlay,
          boxSizing: "border-box",
          color: C.ink,
          fontFamily: F.sans,
          maxHeight: mobile ? "88vh" : "86vh",
          overflowY: "auto",
          padding: mobile ? "10px 16px calc(20px + var(--safe-bottom, 0px))" : 20,
          width: mobile ? "100%" : "min(100%, 460px)",
        }}
      >
        {mobile ? (
          <div style={{ background: C.edge, borderRadius: R.pill, height: 4, margin: "0 auto 12px", width: 40 }} />
        ) : null}
        <div
          style={{
            alignItems: "baseline",
            background: C.card,
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 4,
            position: "sticky",
            top: mobile ? -10 : -20,
            paddingBottom: 6,
            zIndex: 1,
          }}
        >
          <span style={{ ...T.title, fontSize: 22 }}>{title}</span>
          {/* Y1 · a ghost action, like every other text button in the app, in
            * place of an underlined link. */}
          <button data-testid="sheet-done" onClick={onClose} style={{ ...GHOST, fontSize: 15, minHeight: 36, padding: "0 2px" }}>
            {translate("app.done")}
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* One labelled control group: the label sits above its chips rather than
 * beside them, so a long chip row wraps without the label orphaning itself on
 * a line of its own. */
export function SheetRow({ label, hint, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...T.label, color: C.sub, marginBottom: 8 }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{children}</div>
      {hint ? <div style={{ ...T.caption, color: C.sub, marginTop: 7 }}>{hint}</div> : null}
    </div>
  );
}

/* Chip/pill toggle, shared so the Ledger's filters and the app settings read
 * as the same control. 34px tall: compact enough for a dense filter row, but a
 * real touch target. */
export function Chip({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{ ...chip(active), lineHeight: 1.2 }}>
      {children}
    </button>
  );
}

/* The segmented control used for a small set of mutually exclusive choices
 * (the Ledger's side filter, the update mode). Full-height segments rather
 * than the smaller `Chip`, because these are the ones people reach for most. */
export function SegBar({ options, value, onChange, max = 420 }) {
  return (
    <div
      style={{
        background: C.cardDeep,
        border: `1px solid ${C.edge}`,
        borderRadius: R.md,
        display: "flex",
        flex: "1 1 200px",
        gap: 2,
        maxWidth: max,
        padding: 3,
      }}
    >
      {options.map(([key, label]) => {
        const active = value === key;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            style={{
              background: active ? C.card : "transparent",
              border: active ? `1.5px solid ${C.brass}` : "1.5px solid transparent",
              borderRadius: R.sm,
              boxShadow: active ? ELEVATION.card : "none",
              color: active ? C.brass : C.sub,
              cursor: "pointer",
              flex: 1,
              ...T.small,
              fontWeight: 700,
              minHeight: 38,
              padding: "8px 4px",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
